// Relative imports, not the `@/` alias: the Cloudflare Pages Functions in
// `apps/web/functions/` import this module directly so the edge and the client
// emit identical structured data, and esbuild there doesn't know the alias.
import { SITE, absoluteUrl } from '../config/site';
import { ROUTES } from '../config/routes';
import type { JsonLd } from './types';

/**
 * Structured-data builders. These are what turn a blue link into a result with
 * a byline, a date and a thumbnail — Google reads `BlogPosting` for article
 * eligibility and `BreadcrumbList` for the path shown above the title.
 *
 * Stable `@id` values let the nodes reference each other instead of repeating
 * the publisher block in every graph.
 */

const ORGANIZATION_ID = `${SITE.url}/#organization`;
const WEBSITE_ID = `${SITE.url}/#website`;

/** The publisher. Emitted once, statically, from `index.html`. */
export function organizationSchema(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE.name,
    url: SITE.url,
    description: SITE.description,
    logo: {
      '@type': 'ImageObject',
      url: absoluteUrl('/icon-512.png'),
      width: 512,
      height: 512,
    },
    email: SITE.contactEmail,
  };
}

/** The site itself. Also emitted statically from `index.html`. */
export function webSiteSchema(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    name: SITE.name,
    alternateName: SITE.shortName,
    url: SITE.url,
    description: SITE.description,
    inLanguage: SITE.lang,
    publisher: { '@id': ORGANIZATION_ID },
  };
}

export interface ArticleSchemaInput {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  coverUrl: string;
  publishedAt: string | null;
  updatedAt?: string | null;
  readTime: number;
  authorName: string | null;
  /** Plain-text body, used for `wordCount`. */
  plainText?: string;
}

/** A published article. `BlogPosting` is the narrower, better-matched type. */
export function articleSchema(input: ArticleSchemaInput): JsonLd {
  const url = absoluteUrl(ROUTES.article(input.slug));
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    headline: input.title,
    description: input.excerpt || SITE.description,
    articleSection: input.category,
    image: input.coverUrl ? [absoluteUrl(input.coverUrl)] : [absoluteUrl(SITE.ogImage)],
    datePublished: input.publishedAt ?? undefined,
    dateModified: input.updatedAt ?? input.publishedAt ?? undefined,
    author: input.authorName
      ? { '@type': 'Person', name: input.authorName }
      : { '@id': ORGANIZATION_ID },
    publisher: { '@id': ORGANIZATION_ID },
    isPartOf: { '@id': WEBSITE_ID },
    inLanguage: SITE.lang,
    timeRequired: `PT${Math.max(1, input.readTime)}M`,
    ...(input.plainText ? { wordCount: countWords(input.plainText) } : {}),
  };
}

export interface BreadcrumbItem {
  name: string;
  path: string;
}

/** The `Home › Articles › Title` trail Google renders above the result title. */
export function breadcrumbSchema(items: BreadcrumbItem[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export interface BlogListingItem {
  slug: string;
  title: string;
  excerpt: string;
  publishedAt: string | null;
  authorName: string | null;
}

/** The home page as a blog index, with its posts listed in order. */
export function blogSchema(posts: BlogListingItem[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${SITE.url}/#blog`,
    name: SITE.name,
    url: SITE.url,
    description: SITE.description,
    inLanguage: SITE.lang,
    publisher: { '@id': ORGANIZATION_ID },
    blogPost: posts.map((post) => ({
      '@type': 'BlogPosting',
      '@id': `${absoluteUrl(ROUTES.article(post.slug))}#article`,
      url: absoluteUrl(ROUTES.article(post.slug)),
      headline: post.title,
      description: post.excerpt || undefined,
      datePublished: post.publishedAt ?? undefined,
      author: post.authorName
        ? { '@type': 'Person', name: post.authorName }
        : { '@id': ORGANIZATION_ID },
    })),
  };
}

/** The About page, tied back to the organization it describes. */
export function aboutPageSchema(description: string): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${absoluteUrl(ROUTES.about)}#about`,
    url: absoluteUrl(ROUTES.about),
    name: `About ${SITE.name}`,
    description,
    inLanguage: SITE.lang,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': ORGANIZATION_ID },
  };
}

function countWords(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}
